import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CheckCircle, CalendarPlus, MessageCircle } from 'lucide-react';
import LoadingScreen from '../components/LoadingScreen';
import ErrorScreen from '../components/ErrorScreen';
import { formatDOP } from '../lib/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BookingConfirmation() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lateRefund, setLateRefund] = useState(false);
  const [countdown, setCountdown] = useState(8);
  const order = searchParams.get('order');

  useEffect(() => {
    if (!order) {
      navigate('/client/dashboard');
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;

    const checkPayment = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/payments/status/${order}`, {
          credentials: 'include'
        });
        const data = await res.json();

        if (data.payment_status === 'late_refund') {
          // El pago llegó cuando la reserva ya había expirado: se reembolsará.
          setLateRefund(true);
          setLoading(false);
          return;
        }
        if (data.payment_status === 'paid') {
          const bookingRes = await fetch(`${BACKEND_URL}/api/bookings/${data.booking_id}`, {
            credentials: 'include'
          });
          const bookingData = await bookingRes.json();
          setBooking(bookingData);
          setLoading(false);
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkPayment, 2000);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error:', error);
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkPayment, 2000);
        } else {
          setLoading(false);
        }
      }
    };

    checkPayment();
  }, [order, navigate]);

  // La redirección automática se cancela si el usuario interactúa (p. ej.
  // descarga el calendario): no hay que arrebatarle la página.
  const [autoRedirect, setAutoRedirect] = useState(true);

  useEffect(() => {
    if (!booking || !autoRedirect) return;
    if (countdown <= 0) { navigate('/client/dashboard'); return; }
    const tm = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(tm);
  }, [booking, countdown, navigate, autoRedirect]);

  if (loading) {
    return <LoadingScreen message={t('bookingConfirmation.verifyingPayment')} />;
  }

  if (lateRefund) {
    return (
      <ErrorScreen
        title={t('bookingConfirmation.lateRefundTitle', 'Tu pago llegó tarde')}
        message={t('bookingConfirmation.lateRefundMessage', 'Recibimos tu pago, pero la reserva ya había expirado y no pudo confirmarse. Te reembolsaremos el monto completo (5–10 días hábiles). Te enviamos los detalles por correo.')}
        onBack={() => navigate('/client/dashboard')}
        backLabel={t('bookingConfirmation.goToMyBookings')}
      />
    );
  }

  if (!booking) {
    return (
      <ErrorScreen
        title={t('bookingConfirmation.verifyErrorTitle', 'No pudimos confirmar tu pago')}
        message={t('bookingConfirmation.couldNotVerify')}
        onRetry={() => window.location.reload()}
        onBack={() => navigate('/client/dashboard')}
        backLabel={t('bookingConfirmation.goToMyBookings')}
      />
    );
  }

  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-DO';

  // Descarga el .ics vía fetch (no <a href>) para que la cookie/token de
  // sesión viaje igual en ambos ambientes (same-site y cross-site).
  const downloadCalendar = async () => {
    setAutoRedirect(false);
    try {
      const res = await fetch(`${BACKEND_URL}/api/bookings/${booking.id}/calendar.ics`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `redmobility_${booking.booking_code}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('No se pudo descargar el calendario:', e);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 md:px-6 max-w-3xl">
        <button
          onClick={() => navigate('/client/dashboard')}
          className="mb-8 flex items-center gap-2 text-gray-600 hover:text-[#134168] font-medium transition-colors"
        >
          {t('bookingConfirmation.goToMyBookings')}
        </button>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl p-8 md:p-12 shadow-xl text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-16 h-16 text-green-500" />
          </motion.div>

          <h1 className="text-4xl font-heading font-bold text-[#111827] mb-4">{t('bookingConfirmation.title')}</h1>
          <p className="text-xl text-gray-600 mb-8">{t('bookingConfirmation.yourAdventureReady')}</p>

          <div className="bg-gray-50 rounded-2xl p-6 mb-8">
            <p className="text-sm text-gray-500 mb-2">{t('bookingConfirmation.bookingCode')}</p>
            <p className="text-3xl font-heading font-bold text-[#134168]">{booking.booking_code}</p>
          </div>

          <div className="text-left space-y-3 mb-8">
            <div className="flex justify-between py-3 border-b">
              <span className="text-gray-600">{t('bookingConfirmation.vehicle')}</span>
              <span className="font-semibold">{booking.asset?.name || t('bookingConfirmation.notAvailableShort')}</span>
            </div>
            <div className="flex justify-between py-3 border-b">
              <span className="text-gray-600">{t('bookingConfirmation.dateTime')}</span>
              <span className="font-semibold">{new Date(booking.start_datetime).toLocaleString(locale)}</span>
            </div>
            <div className="flex justify-between py-3 border-b">
              <span className="text-gray-600">{t('bookingConfirmation.duration')}</span>
              <span className="font-semibold">
                {booking.end_hours >= 24 && booking.end_hours % 24 === 0
                  ? `${booking.end_hours / 24} ${booking.end_hours / 24 === 1 ? t('listing.day', 'día') : t('listing.days', 'días')}`
                  : `${booking.end_hours} ${t('bookingConfirmation.hours')}`}
              </span>
            </div>
            <div className="flex justify-between py-3 border-b">
              <span className="text-gray-600">{t('bookingConfirmation.total')}</span>
              <span className="font-bold text-xl text-[#134168]">{formatDOP(booking.total_price)}</span>
            </div>
          </div>

          <button
            onClick={downloadCalendar}
            className="mx-auto mb-6 flex items-center gap-2 text-[#134168] font-medium hover:underline"
          >
            <CalendarPlus className="w-5 h-5" />
            {t('bookingConfirmation.addToCalendar', 'Añadir a mi calendario')}
          </button>

          {/* Botón de WhatsApp Directo */}
          <a
            href={`https://wa.me/18095550100?text=${encodeURIComponent(`Hola RedMobility, tengo una consulta sobre mi reserva confirmada ${booking?.booking_code || ''}.`)}`}
            target="_blank"
            rel="noreferrer"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white py-3.5 px-6 rounded-full font-bold mb-4 hover:bg-[#1eb956] transition-colors shadow-sm text-sm"
          >
            <MessageCircle className="w-5 h-5" />
            {t('bookingConfirmation.whatsappSupport', 'Contactar por WhatsApp')}
          </a>

          {autoRedirect && (
            <p className="text-sm text-gray-400 mb-4">
              {t('bookingConfirmation.redirectingIn', { count: countdown })}
            </p>
          )}

          <div className="flex flex-col md:flex-row gap-4">
            <button
              onClick={() => navigate('/client/dashboard')}
              className="flex-1 bg-[#134168] text-white px-6 py-4 rounded-full font-bold hover:bg-[#39b4aa] transition-all"
            >
              {t('bookingConfirmation.viewBookings')}
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex-1 bg-gray-100 text-gray-900 px-6 py-4 rounded-full font-bold hover:bg-gray-200 transition-all"
            >
              {t('bookingConfirmation.backToHome2')}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
